import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { Alert, Note } from '../ui';
import { AuthPage, Label } from './auth-parts';

/** What `POST /auth/device/verify` returns — the same principal `/auth/login` returns. */
export interface VerifiedSignIn {
  id: string;
  email: string;
  name: string;
  kind: 'retailer';
  mustChangePassword: boolean;
}

/**
 * P-07 — Device verification (ON-06a). Shown when `/auth/login` answers
 * `{ deviceVerificationRequired: true, sentTo }` instead of a session: the password
 * was right, and this browser is not trusted for this user.
 *
 * No session exists yet, so this screen talks only to the two public routes. On a
 * correct code the API sets the session and device cookies itself; `onVerified`
 * then does what Sign In does after a successful login.
 */
export function DeviceVerification(
  { sentTo, onVerified, onCancel }:
  { sentTo: string; onVerified: (who: VerifiedSignIn) => void; onCancel: () => void },
) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function verify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      onVerified(await api.post<VerifiedSignIn>('/auth/device/verify', { code }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.device.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setNotice(null);
    try {
      const r = await api.post<{ ok: true; sent: boolean }>('/auth/device/resend');
      setCode('');
      setNotice(r.sent ? t('auth.device.resent') : t('auth.device.resendLimited'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.device.failed'));
    }
  }

  return (
    <AuthPage eyebrow={t('auth.eyebrow')} title={t('auth.device.title')} lede={t('auth.device.body', { sentTo })}>
      <form onSubmit={(e) => void verify(e)} noValidate>
        {error ? <Alert tone="stop">{error}</Alert> : null}
        {notice ? <Alert tone="us">{notice}</Alert> : null}

        <div className="mk-auth-field">
          <Label htmlFor="deviceCode" required>{t('auth.device.code')}</Label>
          <input
            id="deviceCode" required inputMode="numeric" autoComplete="one-time-code"
            pattern="[0-9]*" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        <button className="mk-auth-submit" type="submit" disabled={busy || code.length !== 6}>
          {busy ? t('auth.submitting') : t('auth.device.submit')}
        </button>
      </form>

      <Note style={{ marginTop: 'var(--mk-space-4)' }}>
        {t('auth.device.trustNote')}{' '}
        <button type="button" className="mk-auth-link" onClick={() => void resend()}>
          {t('auth.device.resend')}
        </button>
      </Note>

      <hr className="mk-auth-rule" />
      <div className="mk-auth-foot">
        <button type="button" className="mk-auth-alt" onClick={onCancel}>{t('auth.backToSignIn')}</button>
      </div>
    </AuthPage>
  );
}
